import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { ForbiddenView, NotFoundView } from 'src/views/error';

import { getResourceForPath } from 'src/routes/route-permissions';
import { usePermissions } from 'src/hooks/use-permissions';
import { useActivePlatform } from 'src/hooks/use-active-platform';

type RoleBasedComponentProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

export function RoleBasedGuard({ children, fallback }: RoleBasedComponentProps) {
  const location = useLocation();
  const { canGet } = usePermissions();
  const { isLoading, features } = useActivePlatform();

  const currentResource = getResourceForPath(location.pathname);

  if (isLoading) {
    return null;
  }

  if (currentResource && !features.isResourceAvailable(currentResource)) {
    return <NotFoundView standalone={false} />;
  }

  const hasAccess = currentResource ? canGet(currentResource) : true;

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return <ForbiddenView standalone={false} />;
  }

  return <>{children}</>;
}

export function RoleBasedRoute({ children, fallback }: RoleBasedComponentProps) {
  return <RoleBasedGuard fallback={fallback}>{children}</RoleBasedGuard>;
}
